import { useEffect, useRef, useState } from 'react';

/**
 * useCountUp — animates a numeric value from its previous value to `target`
 * over ~500ms with an ease-out curve (Section 5.7). Only runs on initial
 * mount and on real value changes — never on every re-render.
 *
 * Non-finite values (e.g. undefined counts) render as a dash so loading
 * states never flash a "0".
 */
export function useCountUp(target: number | string | undefined, duration = 500): string {
  const targetNum = typeof target === 'number' && Number.isFinite(target) ? target : null;
  const prevNumRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const [display, setDisplay] = useState<string>(targetNum === null ? '—' : formatNum(0));

  useEffect(() => {
    if (targetNum === null) {
      cancelAnimationFrame(rafRef.current);
      setDisplay('—');
      return;
    }
    const from = prevNumRef.current;
    if (from === targetNum) return;

    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic: 1 - (1-t)^3
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (targetNum - from) * eased;
      setDisplay(formatNum(current));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevNumRef.current = targetNum;
      }
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [targetNum, duration]);

  return display;
}

function formatNum(n: number): string {
  return Math.round(n).toLocaleString();
}
